import { useEffect, useState } from 'react';
import { useDraftFlowStore } from './useDraftFlowStore';
import { useOCPStore } from './useOCPStore';
import { getDraft, getVersion, getSignupFlowTags, getSignupFlowIntendedProjects } from '../api';
import { toast } from 'react-toastify';
import { ITagJson } from '@scale/shared/types/tag';
import { IProjectJson } from '@scale/shared/types/project';

export const useFlowVersionControl = () => {
  const [loadingVersion, setLoadingVersion] = useState<boolean>(false);
  const {
    currentFlow,
    setCurrentFlow,
    currentVersion,
    flowVersions,
    addFlowVersion,
    setUnsavedSignupFlow,
    setFinishedFetchingSignupFlowDraft,
  } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    setCurrentFlow: state.setCurrentFlow,
    currentVersion: state.currentVersion,
    flowVersions: state.flowVersions,
    addFlowVersion: state.addFlowVersion,
    setUnsavedSignupFlow: state.setUnsavedSignupFlow,
    setFinishedFetchingSignupFlowDraft: state.setFinishedFetchingSignupFlowDraft,
  }));
  const { addDraftFlow } = useDraftFlowStore(state => ({
    addDraftFlow: state.addDraftFlow,
  }));

  const [isFirstEffectComplete, setIsFirstEffectComplete] = useState(false);

  useEffect(() => {
    setFinishedFetchingSignupFlowDraft(false);
    if (!isFirstEffectComplete) return;
    (async () => {
      if (
        currentFlow &&
        currentVersion === -1 &&
        currentFlow._id.length &&
        !currentFlow._id.startsWith('tempId_')
      ) {
        setLoadingVersion(true);
        try {
          const { draft, signupFlowDraft } = await getDraft(currentFlow._id);
          if (draft) addDraftFlow(draft);
          if (signupFlowDraft) {
            const tags = await getSignupFlowTags(signupFlowDraft.tags);
            const intendedProjects = await getSignupFlowIntendedProjects(
              signupFlowDraft.intendedProjects,
            );
            setUnsavedSignupFlow({
              ...signupFlowDraft,
              tags:
                tags?.map((tag: ITagJson) => ({
                  label: tag.name,
                  value: tag._id.toString(),
                })) || [],
              intendedProjects:
                intendedProjects?.map((project: IProjectJson) => ({
                  label: project.name,
                  value: project._id.toString(),
                })) || [],
            });
          } else {
            setUnsavedSignupFlow({});
          }
        } catch (e) {
          toast(`Error fetching existing draft: ${e.message}. Creating local draft.`, {
            type: 'error',
            autoClose: 5000,
            position: 'top-right',
          });
          addDraftFlow({
            ...currentFlow,
            originalFlowId: currentFlow._id,
          });
        }
        setLoadingVersion(false);
      } else {
        setUnsavedSignupFlow({});
      }
      setFinishedFetchingSignupFlowDraft(true);
    })();
  }, [currentFlow, currentVersion, isFirstEffectComplete]);

  // Getting and setting the correct version for selected flow should go first
  useEffect(() => {
    setIsFirstEffectComplete(false);
    (async () => {
      if (currentFlow && currentVersion !== currentFlow.version && currentVersion !== -1) {
        if (flowVersions[currentFlow._id][currentVersion])
          setCurrentFlow(flowVersions[currentFlow._id][currentVersion]);
        else {
          setLoadingVersion(true);
          try {
            let flow = await getVersion(currentFlow._id, currentVersion);
            if (flow.originalFlowId) flow = { ...flow, _id: flow.originalFlowId };
            addFlowVersion(flow);
            setCurrentFlow(flow);
          } catch (e) {
            toast(`Error fetching flow version: ${e.message}`, {
              type: 'error',
              autoClose: 5000,
              position: 'top-right',
            });
          } finally {
            setLoadingVersion(false);
          }
        }
      }
    })();
    setIsFirstEffectComplete(true);
  }, [currentFlow, currentVersion, flowVersions, addFlowVersion, setCurrentFlow]);

  return { loadingVersion };
};
